import React from 'react'
import { DeleteFilled, PlusOutlined } from '@ant-design/icons'
import { Button, Input as AntdInput, Tooltip, Typography } from 'antd'
import moment from 'moment-timezone'
import PropTypes from 'prop-types'

import { DatePicker } from '~stories'
import { disabledExpiryDate, disabledExpiryTime } from '../../utils/universalTemplateExpiryPicker'

import styles from './UniversalTemplateFields.module.css'

const { Text } = Typography

const UniversalTemplateFormFields = ({ customFieldsError, expiresAtError, form, formUrlError, onChange, venueTimezone = 'America/New_York' }) => {
  const {
    url = '',
    desktop_height: desktopHeight = '',
    mobile_height: mobileHeight = '',
    custom_fields: customFields = {},
    expires_at: expiresAt = null
  } = form || {}

  const patch = updates => onChange({ ...form, ...updates })

  const customFieldEntries = Object.entries(customFields)

  const handleCustomFieldKeyChange = (oldKey, newKey) => {
    const updated = { ...customFields }
    const value = updated[oldKey]
    delete updated[oldKey]
    updated[newKey] = value
    patch({ custom_fields: updated })
  }

  const handleCustomFieldValueChange = (key, value) => {
    patch({ custom_fields: { ...customFields, [key]: value } })
  }

  const handleAddCustomField = () => {
    const baseKey = 'field'
    let key = baseKey
    let i = 1
    while (Object.prototype.hasOwnProperty.call(customFields, key)) {
      key = `${baseKey}_${i++}`
    }
    patch({ custom_fields: { ...customFields, [key]: '' } })
  }

  const handleDeleteCustomField = key => {
    const updated = { ...customFields }
    delete updated[key]
    patch({ custom_fields: updated })
  }

  return (
    <div className={styles.fieldGroup}>
      <div className={styles.fieldRow}>
        <Text className={[styles.fieldLabel, formUrlError && styles.fieldLabelMandatory].filter(Boolean).join(' ')}>
          Form URL *
        </Text>
        <div className={formUrlError ? styles.mandatoryControlRing : undefined}>
          <AntdInput
            onChange={e => patch({ url: e.target.value })}
            placeholder="https://form.example.com/embed"
            status={formUrlError ? 'error' : undefined}
            value={url}
          />
        </div>
        {formUrlError
          ? <Text className={styles.validationError}>{formUrlError}</Text>
          : <Text className={styles.hint}>Embed or redirect URL for the form</Text>}
      </div>
      <div className={styles.fieldRow}>
        <Text className={styles.fieldLabel}>Desktop height</Text>
        <AntdInput
          onChange={e => patch({ desktop_height: e.target.value })}
          placeholder="e.g. 800"
          value={desktopHeight}
        />
        <Text className={styles.hint}>Optional iframe height for desktop (pixels or CSS value)</Text>
      </div>
      <div className={styles.fieldRow}>
        <Text className={styles.fieldLabel}>Mobile height</Text>
        <AntdInput
          onChange={e => patch({ mobile_height: e.target.value })}
          placeholder="e.g. 1200"
          value={mobileHeight}
        />
        <Text className={styles.hint}>Optional iframe height for mobile (pixels or CSS value)</Text>
      </div>
      <div className={styles.fieldRow}>
        <Text className={[styles.fieldLabel, expiresAtError && styles.fieldLabelMandatory].filter(Boolean).join(' ')}>
          Expiry Time ({moment.tz(venueTimezone).zoneAbbr()}) *
        </Text>
        <div className={expiresAtError ? styles.mandatoryControlRing : undefined}>
          <DatePicker
            allowClear
            disabledDate={disabledExpiryDate(venueTimezone)}
            disabledTime={disabledExpiryTime(venueTimezone)}
            format="YYYY-MM-DD h:mm A"
            label=""
            onChange={momentValue => patch({ expires_at: momentValue ? momentValue.toISOString() : null })}
            showTime
            value={expiresAt ? moment(expiresAt).tz(venueTimezone) : null}
          />
        </div>
        {expiresAtError
          ? <Text className={styles.validationError}>{expiresAtError}</Text>
          : <Text className={styles.hint}>Timezone: {venueTimezone}</Text>
        }
      </div>
      <div className={styles.fieldRow}>
        <div className={styles.customFieldsHeader}>
          <Text className={[styles.fieldLabel, customFieldsError && styles.fieldLabelMandatory].filter(Boolean).join(' ')}>
            Custom Fields
          </Text>
          <Button icon={<PlusOutlined />} onClick={handleAddCustomField} size="small" type="primary">
            Add field
          </Button>
        </div>
        {customFieldsError
          ? <Text className={styles.validationError}>{customFieldsError}</Text>
          : null}
        {customFieldEntries.length === 0 && (
          <Text className={styles.hint}>No custom fields yet. Click &quot;Add field&quot; to create one.</Text>
        )}
        {customFieldEntries.map(([key, value], index) => (
          <div key={index} className={styles.customFieldRow}>
            <div className={styles.customFieldCol}>
              <Text
                className={[
                  styles.customFieldColLabel,
                  customFieldsError && !String(key).trim() && styles.customFieldKeyMissing
                ].filter(Boolean).join(' ')}
              >
                Key *
              </Text>
              <AntdInput
                onChange={e => handleCustomFieldKeyChange(key, e.target.value)}
                placeholder="e.g. utm_source"
                status={customFieldsError && !String(key).trim() ? 'error' : undefined}
                value={key}
              />
            </div>
            <div className={styles.customFieldCol}>
              <Text className={styles.customFieldColLabel}>Value</Text>
              <AntdInput
                onChange={e => handleCustomFieldValueChange(key, e.target.value)}
                placeholder="e.g. homepage"
                value={value}
              />
            </div>
            <Tooltip title="Delete field">
              <Button
                className={styles.customFieldDeleteBtn}
                danger
                icon={<DeleteFilled />}
                onClick={() => handleDeleteCustomField(key)}
                size="small"
                type="text"
              />
            </Tooltip>
          </div>
        ))}
      </div>
    </div>
  )
}

UniversalTemplateFormFields.propTypes = {
  customFieldsError: PropTypes.string,
  expiresAtError: PropTypes.string,
  form: PropTypes.object,
  formUrlError: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  venueTimezone: PropTypes.string
}

export default UniversalTemplateFormFields
